import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { ConfirmacaoModal, FerramentasDeDetalhe, InputError } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { WorkflowsService } from 'shared/services/workflows/workflowsService';
import { useSelectedOrganization } from 'shared/store/organization';
import { ICreateWorkflow, InicitalCreateWorkflow, listKeyValueStatus, listKeyValueTipo } from 'shared/types';
import { t } from 'i18next';
import { dateToLocalISO, stringToDateTime, stringToLocalISO } from 'shared/utils';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, ptBR } from 'date-fns/locale';
import env from 'config/env';
import { useHasPermissionAcao } from 'shared/hooks';


const validationSchema = yup.object({
  NomeWorkflow: yup.string().required(),
  DescricaoWorkflow: yup.string().required(),
  StatusWorkflow: yup.string().required(),
  TipoWorkflow: yup.string().required(),
  DhLiberadoEnvio: yup.string().required(),
  DhPeriodoInicio: yup.string().required(),
  DhPeriodoFim: yup.string().required(),
});

export const DetalheDeWorkflows: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const org = useSelectedOrganization();

  const localeSel = (env.language === 'en' ? enUS : ptBR);
  const localeMask = (env.language === 'en' ? '__/__/____ __:__:__ _M' : '__/__/____ __:__:__');
  const localeInputFormat = (env.language === 'en' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss');

  const lenDate = (env.language === 'en' ? 22 : 19);

  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');


  const hasPermitionCreate = useHasPermissionAcao('workflow','CRE');
  const hasPermitionDelete = useHasPermissionAcao('workflow','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('workflow','UPD');

  const selectStatus = watch('StatusWorkflow');
  const selectTipo = watch('TipoWorkflow');
  const dateTimePickerInicio = watch('DhPeriodoInicio');
  const dateTimePickerFim = watch('DhPeriodoFim');
  const dateTimePickerLibEnv = watch('DhLiberadoEnvio');
  const dateTimePickerProxExec = watch('DhProximaExecucao');
  const dateInclusao = stringToDateTime(watch('DhInclusao'));
  const dateAlteracao = watch('NomeUsuarioUpd') === '' ? '--' : stringToDateTime(watch('DhAlteracao'));
  const usuarioAlteracao = watch('NomeUsuarioUpd') === '' ? '--' : watch('NomeUsuarioUpd');

  const handleChangeDhInicio = (valor: string) => setValue('DhPeriodoInicio', valor);
  const handleChangeDhFim = (valor: string) => setValue('DhPeriodoFim', valor);
  const handleChangeDhLibEnv = (valor: string) => setValue('DhLiberadoEnvio', valor);
  const handleChangeDhProxExec = (valor: string) => setValue('DhProximaExecucao', valor);

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      WorkflowsService.getById(Number(org?.CodigoOrganizacao), Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/workflow/listagem');
          } else {
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateWorkflow);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.workflow.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    WorkflowsService.deleteById(Number(org?.CodigoOrganizacao), Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'error',
          });
          navigate('/workflow/listagem');
        }
      })
      .catch(reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateWorkflow) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      WorkflowsService
        .create(Number(org?.CodigoOrganizacao), data)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/workflow/listagem');
            } else {
              navigate(`/workflow/detalhe/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      WorkflowsService
        .updateById(Number(org?.CodigoOrganizacao), Number(id), data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/workflow/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'),
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Workflow' : (selectStatus === 'INAT ' && hasPermitionUpdate) ? 'Alterar Workflow' : 'Consultar Workflow'}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeWorkflow'
          textoBotaoNovo={t('components.actions.novo')}
          textoBotaoGenerico='Fluxo'
          iconeBotaoGenerico='alt_route'

          mostrarBotaoNovo={id !== 'novo' && hasPermitionCreate}
          mostrarBotaoApagar={id !== 'novo' && selectStatus === 'INAT ' && hasPermitionDelete}
          mostrarBotaoGenerico={id !== 'novo'}
          mostrarBotaoSalvar={selectStatus === 'INAT ' && (hasPermitionCreate || hasPermitionUpdate)}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/workflow/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/workflow/detalhe/novo')}
          aoClicarEmGenerico={() => navigate(`/workflow/detalhe/${id}/flow`)}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeWorkflow' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.NomeWorkflow?.type ? true : false}
                      fullWidth
                      id='NomeWorkflow'
                      type='text'
                      label={`${t('pages.workflows.nomeWorkflow')} *`}
                      disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('NomeWorkflow')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.NomeWorkflow?.type && <InputError type={errors.NomeWorkflow.type} field='NomeWorkflow' />}

                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-status'>{`${t('pages.workflows.statusWorkflow')}`}</InputLabel>
                      <Select
                        disabled={true}
                        error={errors?.StatusWorkflow?.type ? true : false}
                        labelId='label-select-status'
                        id='StatusWorkflow'
                        input={<OutlinedInput label={`${t('pages.workflows.statusWorkflow')} *`} />}
                        {...register('StatusWorkflow')}
                        value={selectStatus || 'INAT '}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueStatus.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.StatusWorkflow?.type && <InputError type={errors.StatusWorkflow.type} field='StatusWorkflow' />}

                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-tipo'>{`${t('pages.workflows.tipoWorkflow')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                        error={errors?.TipoWorkflow?.type ? true : false}
                        labelId='label-select-tipo'
                        id='TipoWorkflow'
                        input={<OutlinedInput label={`${t('pages.workflows.tipoWorkflow')} *`} />}
                        {...register('TipoWorkflow')}
                        value={selectTipo || ''}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueTipo.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.TipoWorkflow?.type && <InputError type={errors.TipoWorkflow.type} field='TipoWorkflow' />}

                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.DescricaoWorkflow?.type ? true : false}
                      fullWidth
                      multiline={true}
                      rows={2}
                      id='DescricaoWorkflow'
                      type='text'
                      label={`${t('pages.workflows.descricaoWorkflow')} *`}
                      disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('DescricaoWorkflow')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.DescricaoWorkflow?.type && <InputError type={errors.DescricaoWorkflow.type} field='DescricaoWorkflow' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }} >
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                          label={`${t('pages.workflows.dhPeriodoInicio')} *`}
                          value={dateTimePickerInicio}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhInicio(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhInicio((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) =>
                            <TextField
                              id='DhPeriodoInicio'
                              {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.DhPeriodoInicio?.type && <InputError type={errors.DhPeriodoInicio.type} field='DhPeriodoInicio' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                          label={`${t('pages.workflows.dhPeriodoFim')} *`}
                          value={dateTimePickerFim}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhFim(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhFim((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) => <TextField id='DhPeriodoFim' {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.DhPeriodoFim?.type && <InputError type={errors.DhPeriodoFim.type} field='DhPeriodoFim' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={isSubmitting || isLoading || selectStatus !== 'INAT ' || (!hasPermitionUpdate && id !== 'novo')}
                          label={`${t('pages.workflows.dhLiberadoEnvio')} *`}
                          value={dateTimePickerLibEnv}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhLibEnv(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhLibEnv((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) => <TextField id='DhLiberadoEnvio' {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.DhLiberadoEnvio?.type && <InputError type={errors.DhLiberadoEnvio.type} field='DhLiberadoEnvio' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    {id !== 'novo' && (<FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={true}
                          label={t('pages.workflows.dhProximaExecucao')}
                          value={dateTimePickerProxExec || null}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhProxExec(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhProxExec((newValue === null) ? '' : dateToLocalISO(newValue))}

                          renderInput={(params) => <TextField id='DhProximaExecucao' {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>)}
                    {errors?.DhProximaExecucao?.type && <InputError type={errors.DhProximaExecucao.type} field='DhProximaExecucao' />}
                  </Grid>

                  {id !== 'novo' && (
                    <>
                      <Grid item xs={3} paddingX={2}>
                        <TextField
                          fullWidth
                          id='NomeUsuarioIns'
                          type='text'
                          label={t('pages.workflows.nomeUsuarioIns')}
                          disabled={true}
                          {...register('NomeUsuarioIns')}
                          sx={{ marginTop: '1rem', marginBottom: '0' }}
                        />
                      </Grid>
                      <Grid item xs={3} paddingX={2}>
                        <TextField
                          fullWidth
                          id='DhInclusao'
                          type='text'
                          label={t('pages.workflows.dhInclusao')}
                          disabled={true}
                          value={dateInclusao}
                          sx={{ marginTop: '1rem', marginBottom: '0' }}
                        />
                      </Grid>
                      <Grid item xs={3} paddingX={2}>
                        <TextField
                          fullWidth
                          id='NomeUsuarioUpd'
                          type='text'
                          label={t('pages.workflows.nomeUsuarioUpd')}
                          disabled={true}
                          value={usuarioAlteracao}
                          sx={{ marginTop: '1rem', marginBottom: '0' }}
                        />
                      </Grid>
                      <Grid item xs={3} paddingX={2}>
                        <TextField
                          fullWidth
                          id='DhAlteracao'
                          type='text'
                          label={t('pages.workflows.dhAlteracao')}
                          disabled={true}
                          value={dateAlteracao}
                          sx={{ marginTop: '1rem', marginBottom: '0' }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}
    </LayoutBaseDePagina >
  );
};
